import { z } from 'zod';

import { LocaleSchema } from '../common/locale/locale';
import { GenderSchema } from '../users/gender';

import { ProfileQuestionSchema } from './profile-question';
import { QuestionFlowSchema } from './question-flow/question-flow';
import { QuestionSectionSchema } from './question-section';

/** One section of a composed form, with the questions this member is actually asked. */
export const ComposedFormSectionSchema = QuestionSectionSchema.extend({
  questions: z.array(ProfileQuestionSchema),
}).strict();

export type ComposedFormSection = z.infer<typeof ComposedFormSectionSchema>;

/**
 * A whole flow, ready to render (A4): active sections in order, each holding the
 * active questions for this member's gender, in order, with labels in their
 * locale, options resolved, and **requiredness already decided** — including the
 * gender-scoped kind (`requiredSettingKey`, U15). Mobile draws this; it decides
 * nothing.
 *
 * It deliberately carries **no answers**. The member's answers live in
 * `modules/profiles` and are fetched alongside (`GET /profile/answers`), for two
 * reasons: U1 keeps the two modules apart, and — more practically — a form is
 * the same object for every member of a gender and locale, so it caches, while
 * answers change on every keystroke and must not drag the form's cache down with
 * them.
 *
 * `gender` and `locale` are echoed back because the payload is only valid for
 * that pair: a client that switched language mid-wizard must be able to tell
 * that what it holds is stale.
 */
export const ComposedFormSchema = z
  .object({
    flow: QuestionFlowSchema,
    gender: GenderSchema,
    locale: LocaleSchema,
    sections: z.array(ComposedFormSectionSchema),
    /** How many questions in this flow are required of this member — the denominator of progress. */
    requiredQuestionCount: z.number().int().min(0),
  })
  .strict();

export type ComposedForm = z.infer<typeof ComposedFormSchema>;
